import { useEffect, useState } from "react";
import { Button, DatePicker, Skeleton, Steps } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../store/types";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLazyGetMyServicesQuery } from "../../services/service";
import useScreenSize from "../../hooks/useScreenSize";
import { FilterFilled, FilterOutlined } from "@ant-design/icons";

const MySerials = () => {
    const { t } = useTranslation();
    const { isMobile } = useScreenSize();
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const pageSize: number = 10;
    const [pageNo, setPageNo] = useState<number>(1);
    const [more, setMore] = useState<boolean>(true);
    const [dataSource, setDataSource] = useState<any>([]);
    const [filtered, setFiltered] = useState<boolean>(false);
    const [serviceDate, setServiceDate] = useState<any>(null);
    const [getMyServices, getMyServicesResult] = useLazyGetMyServicesQuery();

    useEffect(() => {
        if (loggedInUser?.id) {
            getMyServices({ pageSize, pageNo, serviceDate: serviceDate?.format("YYYY-MM-DD") });
        }
    }, [pageSize, pageNo, serviceDate]);

    useEffect(() => {
        if (getMyServicesResult.isSuccess) {
            const sData = getMyServicesResult.data || [];
            if (pageNo === 1) {
                setDataSource(sData);
            } else {
                setDataSource((prevData: any[]) => [...prevData, ...sData.filter((x: any) => !prevData.some((s: any) => s.id === x.id))]);
            }
            setMore(sData.length >= pageSize);
        }
    }, [getMyServicesResult.isLoading, getMyServicesResult.isFetching])

    const loadMoreData = () => {
        setPageNo(prevValue => prevValue + 1);
    };

    const onServiceDateChanged = (value: any) => {
        setServiceDate(value);
        setPageNo(1);
    };

    const onFilterClicked = () => {
        setFiltered(!filtered);
        if (!filtered) {
            setServiceDate(dayjs());
            setPageNo(1);
        } else {
            setServiceDate(null);
            setPageNo(1);
        }
    };

    return (
        <>
            <div className="flex justify-between flex-wrap items-center gap-4">
                <h2 className="pt-0 mt-0 pb-0 mb-0">Serial History</h2>
                <Button type="link" onClick={onFilterClicked}>{!filtered ? <FilterOutlined /> : <FilterFilled />}</Button>
            </div>
            {filtered && <div className="flex justify-between flex-wrap items-center gap-4 mt-[10px]">
                <div className={`${isMobile ? "w-full" : "w-[50%]"}`}>
                    <span className="w-[100px] inline-block">Serial Date:</span>
                    <DatePicker
                        style={{ width: "calc(100% - 100px)", maxWidth: "200px" }}
                        defaultValue={serviceDate}
                        format={"DD/MM/YYYY"}
                        onChange={onServiceDateChanged}
                    />
                </div>
            </div>}
            <div className="flex justify-between flex-wrap items-center gap-4 pb-2">
                <InfiniteScroll
                    dataLength={dataSource.length}
                    next={loadMoreData}
                    hasMore={more}
                    loader={<Skeleton active />}
                    endMessage={<fieldset className="p-0 m-0 border-0" style={{ borderTop: "1px inset white", color: "#ccc" }}><legend className="text-center">{t("The end")}</legend></fieldset>}
                >
                    {getMyServicesResult.isLoading ? <Skeleton active avatar></Skeleton> :
                        dataSource?.length > 0 ?
                            <Steps
                                progressDot={true}
                                current={dataSource.length} direction="vertical"
                                items={dataSource.map((service: any, index: number) => (
                                    {
                                        title: (<div className="actions mr-[15px]">
                                            <span>{dayjs(service.date)?.format("DD/MM/YYYY")}</span>
                                        </div>),
                                        description: <div id={service.id}>
                                            <h4 style={{ marginTop: "5px", marginBottom: "10px" }} >
                                                {service.company?.businessTypeId === 1 && <div>
                                                    {!isMobile && <><label className="text-primary !w-[120px]" style={{ display: "inline-block" }}>Organization</label>:</>}
                                                    <span className={`${isMobile ? "" : "pl-[10px]"}`}>{service.company?.name}</span>
                                                </div>}
                                                <div>
                                                    {!isMobile && <><label className="text-primary !w-[120px]" style={{ display: "inline-block" }}>Service Center</label>:</>}
                                                    <span className={`${isMobile ? "text-primary" : "pl-[10px]"}`}>{service.serviceCenter.name}</span>
                                                </div>
                                                {!service.forSelf && <div>
                                                    {isMobile && <>For {service.name}</>}
                                                    {!isMobile && <>
                                                        <label className="text-primary !w-[120px]" style={{ display: "inline-block" }}>For</label>:
                                                        <span className={"pl-[10px]"}>{service.name}</span>
                                                    </>}
                                                </div>}
                                                <div>
                                                    {!isMobile && <><label className="text-primary !w-[120px]" style={{ display: "inline-block" }}>Service Type</label>:</>}
                                                    <span className={`${isMobile ? "" : "pl-[10px]"}`}>{service.serviceType.name}</span>
                                                </div>
                                                <div>
                                                    {!isMobile && <><label className="text-primary !w-[120px]" style={{ display: "inline-block" }}>Serial No.</label>:</>}
                                                    {!isMobile && <span className={"pl-[10px]"}>{service.serialNo}</span>}
                                                    {isMobile && <span>Serial No.: {service.serialNo}</span>}
                                                </div>
                                                <div>
                                                    {!isMobile && <><label className="text-primary !w-[120px]" style={{ display: "inline-block" }}>Status</label>:</>}
                                                    {!isMobile && <span className={"pl-[10px]"}>{service.status} at {dayjs(service.statusTime)?.format("hh:mm: ss A")}</span>}
                                                    {isMobile && <>{service.status} at {dayjs(service.statusTime)?.format("hh:mm: ss A")}</>}
                                                </div>
                                                {service.comment && <div>Comment: <p>{service.comment}</p></div>}
                                            </h4>
                                        </div>
                                    }
                                ))}
                            >
                            </Steps>
                            : (<h3>{t("No serial found.")}</h3>)
                    }
                </InfiniteScroll>
            </div>
        </>
    );
};

export default MySerials;
